/**
 * Voice Service
 *
 * Proxies WebRTC SDP negotiation for the Azure realtime voice model through
 * our own (already-authenticated) backend — see EVT-111.
 */

import type {
  VoiceSessionClientEvent,
  VoiceSessionRequest,
  VoiceSessionResponse,
} from '@evaltick/shared';
import { BaseAPIClient, type RequestOptions } from './base';

export type { VoiceSessionClientEvent, VoiceSessionRequest, VoiceSessionResponse };

export class VoiceService extends BaseAPIClient {
  async negotiateSession(
    request: VoiceSessionRequest,
    options?: RequestOptions
  ): Promise<VoiceSessionResponse> {
    return this.post<VoiceSessionResponse>('/voice/session', request, options);
  }

  /**
   * "This call ended" beacon — lets the backend close the voice observability
   * span promptly instead of leaving it open until its length-cap timer fires.
   * Not sending it is harmless (the cap closes the session either way), so a
   * failure here is swallowed: voice teardown must proceed regardless. The
   * session's duration isn't sent — the backend times it from its own
   * span-start, which a client can't skew.
   */
  async endSession(callId: string): Promise<void> {
    try {
      await this.post('/voice/session/end', { callId });
    } catch {
      // best-effort — see doc comment above
    }
  }

  /**
   * Reports a client-observed health event (dropped connection, mic off…) for
   * the server log, keyed by callId. Best-effort like endSession.
   */
  async reportEvent(report: VoiceSessionClientEvent, options?: RequestOptions): Promise<void> {
    try {
      await this.post('/voice/session/event', report, options);
    } catch {
      // best-effort — see doc comment above
    }
  }
}

export default VoiceService;
